// ClaimSummaryPreview.js — REPORT-OUTPUTS-1 (2026-05-19)
//
// Preview-first surface for the Claim Summary report.  Fetches the
// canonical payload from GET /claims/{id}/reports/claim-summary and
// renders the brand band + claim metadata + financial trio + policy &
// coverage + parties sections in-browser.  Mirrors the layout of the
// XLSX and PDF outputs so users see the same thing they're about to
// download.
//
// Primary actions: Download PDF · Download Excel · Print · Close
// Overflow ("More actions"): Save to Documents · Email Report
//   — both DISABLED with tooltips.  Not fake workflows; communicate the
//   shape of future actions per Kuhrt's product direction.
//
// Print: window.print() invoked from a Print button.  A scoped
// @media print stylesheet hides every element except the preview body.

import { useEffect, useRef, useState } from "react";
import { apiFetch, fmt } from "../api";
import ComposeEmailModal from "./ComposeEmailModal";

const PRINT_STYLE = `
.cs-preview-print-target,
.cs-preview-print-target * {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  color-adjust: exact !important;
}
@media print {
  body * { visibility: hidden !important; }
  .cs-preview-print-target,
  .cs-preview-print-target * { visibility: visible !important; }
  .cs-preview-print-target {
    position: absolute !important;
    left: 0 !important; top: 0 !important;
    width: 100% !important;
    box-shadow: none !important;
    border: none !important;
  }
  .cs-preview-actions { display: none !important; }
}
`;

// Fallback palette — used when the branding payload is missing/empty.
const FALLBACK_NAVY = "#1F3864";
const FALLBACK_ACCENT = "#2E75B6";
const FALLBACK_HEADER_TEXT = "#FFFFFF";
const BG_ALT = "#F8F9FB";
const BORDER_GRAY = "#D0D7E0";
const TEXT_MUTED = "#777";
const TEXT_BODY = "#1A1A1A";

function resolveTheme(branding) {
  const b = branding || {};
  const primary = b.primary_color || FALLBACK_NAVY;
  const secondary = b.secondary_color || FALLBACK_ACCENT;
  return {
    primary,
    secondary,
    headerBg:      b.table_header_bg_color || primary,
    headerText:    b.table_header_text_color || FALLBACK_HEADER_TEXT,
    sectionColor:  b.section_heading_color || primary,
    footerAccent:  b.footer_accent_color || secondary,
  };
}

export default function ClaimSummaryPreview({ claimId, canEdit = false, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [flash, setFlash] = useState(null);
  const [compose, setCompose] = useState(null);   // {document_ids, subject} when emailing

  // Generate the report PDF → file it into the claim's Documents Center. Returns
  // {document_id, filename}. Shared by "Save to Documents" and "Email Report".
  async function fileReport() {
    const r = await apiFetch(`/claims/${claimId}/reports/claim-summary/to-documents`, { method: "POST", body: {} });
    return r;
  }
  async function saveToDocuments() {
    if (busy) return; setBusy(true); setMenuOpen(false); setFlash(null);
    try { const r = await fileReport(); setFlash({ type: "ok", text: `Saved “${r.filename}” to Documents.` }); }
    catch (e) { setFlash({ type: "err", text: e.message || "Could not save report." }); }
    setBusy(false);
  }
  async function emailReport() {
    if (busy) return; setBusy(true); setMenuOpen(false); setFlash(null);
    try {
      const r = await fileReport();
      const cnum = data && data.claim ? data.claim.claim_number : "";
      setCompose({ document_ids: [r.document_id], subject: `Claim Summary — ${cnum}` });
    } catch (e) { setFlash({ type: "err", text: e.message || "Could not prepare the email." }); }
    setBusy(false);
  }

  useEffect(() => {
    let alive = true;
    apiFetch(`/claims/${claimId}/reports/claim-summary`)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e.message || String(e)); });
    return () => { alive = false; };
  }, [claimId]);

  // Close overflow menu on outside-click
  useEffect(() => {
    function onClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  async function download(format) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiFetch(
        `/claims/${claimId}/reports/claim-summary/export?format=${format}`,
        { raw: true }
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(disp);
      a.download = m ? m[1] : `claim-summary.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Download failed: " + (e.message || e));
    } finally {
      setBusy(false);
    }
  }

  function doPrint() {
    window.print();
  }

  if (err) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ color: "#B00020", marginBottom: 12 }}>Couldn't load Claim Summary: {err}</div>
        <button className="btn btn-outline btn-sm" onClick={onClose}>← Back to Claim Reports</button>
      </div>
    );
  }
  if (!data) {
    return <div style={{ padding: 20, color: TEXT_MUTED }}>Loading Claim Summary…</div>;
  }

  const claim = data.claim;
  const fin = data.financial;
  const branding = data.branding || {};
  const theme = resolveTheme(branding);
  const orgName = branding.org_name || "Walters Northwest Claims Services";

  return (
    <div style={{ padding: 8 }}>
      <style>{PRINT_STYLE}</style>

      {/* Action bar */}
      <div className="cs-preview-actions" style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap",
      }}>
        <button className="btn btn-outline btn-sm" onClick={onClose} title="Back to Claim Reports">
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => download("pdf")}
                title="Download as PDF">
          ⤓ PDF
        </button>
        <button className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => download("xlsx")}
                title="Download as Excel">
          ⤓ Excel
        </button>
        <button className="btn btn-outline btn-sm"
                onClick={doPrint}
                title="Print this report">
          🖨 Print
        </button>

        {/* Overflow — both items FILE a PDF onto the claim (a claim document
            write). H17: hidden without an edit cap on this claim; the server
            refuses them there anyway, and offering a button that must 403 is how
            an owner ends up with an undeletable PDF on someone else's claim.
            PDF / Excel / Print above are reads and stay available. */}
        {canEdit && <div ref={menuRef} style={{ position: "relative" }}>
          <button className="btn btn-outline btn-sm"
                  onClick={() => setMenuOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}>
            More actions ▾
          </button>
          {menuOpen && (
            <div style={{
              position: "absolute", right: 0, top: "calc(100% + 4px)",
              background: "white", border: `1px solid ${BORDER_GRAY}`,
              borderRadius: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
              minWidth: 240, zIndex: 50,
              padding: 4,
            }} role="menu">
              <button className="btn btn-outline btn-sm" style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 4 }}
                      onClick={saveToDocuments} disabled={busy}>💾 Save to Documents</button>
              <button className="btn btn-outline btn-sm" style={{ display: "block", width: "100%", textAlign: "left" }}
                      onClick={emailReport} disabled={busy}>✉ Email Report</button>
            </div>
          )}
        </div>}
      </div>
      {flash && <div style={{ margin: "8px 0", padding: "7px 12px", borderRadius: 6, fontSize: 13,
        background: flash.type === "ok" ? "#dcfce7" : "#fee2e2", color: flash.type === "ok" ? "#166534" : "#991b1b" }}>{flash.text}</div>}
      {compose && <ComposeEmailModal claimId={claimId} claim={data ? data.claim : null}
        prefill={{ subject: compose.subject, document_ids: compose.document_ids }}
        onClose={() => setCompose(null)} onSent={() => { setCompose(null); setFlash({ type: "ok", text: "Report emailed." }); }} />}

      {/* Printable preview body */}
      <div className="cs-preview-print-target" style={{
        background: "white",
        border: `1px solid ${BORDER_GRAY}`,
        borderRadius: 4,
        padding: 28,
        maxWidth: 880,
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        color: TEXT_BODY,
      }}>
        {/* Brand band — letterhead image if available, else text */}
        <div style={{ borderBottom: `1px solid ${BORDER_GRAY}`, paddingBottom: 12, marginBottom: 14 }}>
          <LetterheadOrText branding={branding} orgName={orgName} primary={theme.primary} />
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: TEXT_BODY }}>
            Claim Summary Report
          </div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontStyle: "italic", marginTop: 2 }}>
            Claim {claim.claim_number}
          </div>
          <div style={{ fontSize: 10, color: "#555", marginTop: 6 }}>
            Generated by {data.meta.generated_by}  ·  {data.meta.generated_at.replace("T", " ").substring(0, 16)} UTC
          </div>
        </div>

        {/* Claim metadata */}
        <MetaTable pairs={[
          ["Claim #",        claim.claim_number],
          ["Insured",        claim.insured_name || "(no Insured attached)"],
          ["Claimant",       claim.claimant_name || "(unknown)"],
          ["Status",         claim.claim_status || ""],
          ["Date of Loss",   fmt.date(claim.date_of_loss)],
          ["Date Opened",    fmt.date(claim.date_opened)],
          ["Date Closed",    fmt.date(claim.date_closed)],
          ["Lead Adjuster",  claim.lead_adjuster_name || ""],
          ["Vessel",         claim.vessel_name || ""],
          ["Category",       claim.claim_type || ""],
          ["Loss State",     claim.loss_state || ""],
        ]} />

        {/* Financial Summary */}
        <SectionHeading color={theme.sectionColor}>Financial Summary</SectionHeading>
        <MetaTable pairs={[
          ["Total Reserve",     fmt.money(fin.total_reserves)],
          ["Total Payments",    fmt.money(fin.total_paid)],
          ["Remaining Reserve", fmt.money(fin.remaining_reserves)],
        ]} valueAlign="right" />

        {/* Policy & Coverage */}
        <SectionHeading color={theme.sectionColor}>Policy & Coverage</SectionHeading>
        {data.policies.length === 0 ? (
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontStyle: "italic" }}>
            No policy attached to this claim.
          </div>
        ) : data.policies.map((p, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <MetaTable pairs={[
              ["Policy #", p.policy_number || "(unknown)"],
              ["Period",   `${fmt.date(p.inception_date)} to ${fmt.date(p.expiration_date)}`],
              ["Type",     p.policy_type || ""],
              ["Layer",    (p.layer_type || "").charAt(0).toUpperCase() + (p.layer_type || "").slice(1) ||
                           (p.is_primary ? "Primary" : "")],
              ...(p.policy_notes ? [["Policy Notes", p.policy_notes]] : []),
            ]} />
          </div>
        ))}

        {data.coverages.length > 0 && (
          <CoverageTable rows={data.coverages} theme={theme} />
        )}

        {/* Parties */}
        <SectionHeading color={theme.sectionColor}>Parties</SectionHeading>
        {data.parties.length === 0 ? (
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontStyle: "italic" }}>
            No parties recorded.
          </div>
        ) : (
          <PartiesTable rows={data.parties} theme={theme} />
        )}

        {/* Confidentiality line (centered, end of body) */}
        <ConfidentialityLine branding={branding} />

        {/* Branded contact block at the bottom of the preview pane —
            visually analogous to the per-page footer in the PDF/XLSX. */}
        <PageFooter branding={branding} theme={theme} />
      </div>
    </div>
  );
}

function LetterheadOrText({ branding, orgName, primary }) {
  // Branding assets live behind the auth-gated GET /branding/{filename}
  // route — a plain <img src> can't send the JWT bearer header so it 401s.
  // Fetch via apiFetch, build an object URL, then render the <img>.
  const [blobUrl, setBlobUrl] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const url = branding && branding.letterhead_available && branding.letterhead_url;
  useEffect(() => {
    if (!url) { setBlobUrl(null); return; }
    let revokedUrl = null;
    let alive = true;
    apiFetch(url, { raw: true })
      .then(r => r.blob())
      .then(blob => {
        if (!alive) return;
        revokedUrl = URL.createObjectURL(blob);
        setBlobUrl(revokedUrl);
      })
      .catch(() => { if (alive) setImgErr(true); });
    return () => {
      alive = false;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [url]);

  if (blobUrl && !imgErr) {
    return (
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <img src={blobUrl}
             alt={orgName}
             onError={() => setImgErr(true)}
             style={{
               display: "inline-block",
               width: "50%",
               maxWidth: "50%",
               height: "auto",
             }} />
      </div>
    );
  }
  if (imgErr || !url) {
    return (
      <div style={{ fontSize: 16, fontWeight: 700, color: primary, lineHeight: 1.2 }}>
        {orgName}
      </div>
    );
  }
  // url present but blob not yet ready — render placeholder spacer
  return <div style={{ height: 60 }} />;
}

function ConfidentialityLine({ branding }) {
  const text = (branding && branding.confidentiality_footer) ||
    "Confidential — HIPAA-protected information. Distribute only as authorized.";
  return (
    <div style={{
      marginTop: 18, paddingTop: 8,
      fontSize: 9, fontStyle: "italic", color: TEXT_MUTED,
      textAlign: "center",
    }}>
      {text}
    </div>
  );
}

function PageFooter({ branding, theme }) {
  // Visible at the bottom of the preview pane — visually analogous to
  // the PDF's per-page canvas footer and the XLSX print-page footer.
  // Two-column layout matches the WNW letterhead:
  //   Left:  Office / Fax / website (left-justified)
  //   Right: Address / City-State-Zip / newclaims@... (right-justified)
  // Tax ID intentionally OMITTED on reports (invoices only).
  const b = branding || {};
  const leftLines = [];
  if (b.phone)             leftLines.push(`Office: ${b.phone}`);
  if (b.fax)               leftLines.push(`Fax: ${b.fax}`);
  if (b.website_corporate) leftLines.push(b.website_corporate);

  const rightLines = [];
  if (b.address_line1)  rightLines.push(b.address_line1);
  if (b.address_line2)  rightLines.push(b.address_line2);
  if (b.city_state_zip) rightLines.push(b.city_state_zip);
  if (b.email)          rightLines.push(b.email);

  const hasContact = leftLines.length > 0 || rightLines.length > 0;
  if (!hasContact) return null;
  const rowCount = Math.max(leftLines.length, rightLines.length);

  return (
    <div style={{
      marginTop: 30,
      borderTop: `1px solid ${theme.footerAccent}`,
      paddingTop: 6,
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 8.5, color: "#555" }}>
        <tbody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <tr key={i}>
              <td style={{ textAlign: "left",  padding: "1px 0", lineHeight: 1.5 }}>
                {leftLines[i] || ""}
              </td>
              <td style={{ textAlign: "right", padding: "1px 0", lineHeight: 1.5 }}>
                {rightLines[i] || ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeading({ children, color }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 700, color: color || FALLBACK_NAVY,
      marginTop: 18, marginBottom: 6,
    }}>{children}</div>
  );
}

function MetaTable({ pairs, valueAlign = "left" }) {
  return (
    <table style={{ borderCollapse: "collapse", width: "100%" }}>
      <tbody>
        {pairs.map(([k, v], i) => (
          <tr key={i}>
            <td style={{
              fontWeight: 700, fontSize: 10, color: "#333",
              padding: "2px 8px 2px 0", verticalAlign: "top",
              width: "30%",
            }}>{k}:</td>
            <td style={{
              fontSize: 10, color: "#555",
              padding: "2px 0",
              textAlign: valueAlign,
            }}>{v ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CoverageTable({ rows, theme }) {
  const t = theme || resolveTheme(null);
  return (
    <table style={{
      borderCollapse: "collapse", width: "100%",
      marginTop: 10, fontSize: 10,
      border: `1px solid ${BORDER_GRAY}`,
    }}>
      <thead>
        <tr style={{ background: t.headerBg, color: t.headerText }}>
          <Th align="left" theme={t}>Coverage Type</Th>
          <Th align="right" theme={t}>Limit</Th>
          <Th align="right" theme={t}>Per-Claim Deductible</Th>
          <Th align="center" theme={t}>Applies to Loss</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c, i) => {
          const limit = (c.coverage_amount != null && c.coverage_amount > 0)
            ? fmt.money(c.coverage_amount) : "—";
          const ded = c.per_claim_deductible != null
            ? fmt.money(c.per_claim_deductible) : "—";
          const applies = c.applies_to_loss ? "Yes" : "—";
          return (
            <tr key={i} style={{ background: i % 2 ? BG_ALT : "white" }}>
              <Td align="left">{c.coverage_type}</Td>
              <Td align="right">{limit}</Td>
              <Td align="right">{ded}</Td>
              <Td align="center">{applies}</Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PartiesTable({ rows, theme }) {
  const t = theme || resolveTheme(null);
  return (
    <table style={{
      borderCollapse: "collapse", width: "100%",
      marginTop: 6, fontSize: 10,
      border: `1px solid ${BORDER_GRAY}`,
    }}>
      <thead>
        <tr style={{ background: t.headerBg, color: t.headerText }}>
          <Th align="left" theme={t}>Role</Th>
          <Th align="left" theme={t}>Name</Th>
          <Th align="right" theme={t}>Participation %</Th>
          <Th align="left" theme={t}>Reference #</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => {
          let role = p.role;
          if (p.is_lead && p.role === "Lead Underwriter") role = "Lead Underwriter";
          else if (p.is_lead) role = `${p.role} (Lead)`;
          const name = p.company_name || p.contact_name || "(unnamed)";
          const pct = p.participation_pct != null ? `${p.participation_pct.toFixed(1)}%` : "—";
          return (
            <tr key={i} style={{ background: i % 2 ? BG_ALT : "white" }}>
              <Td align="left">{role}</Td>
              <Td align="left">{name}</Td>
              <Td align="right">{pct}</Td>
              <Td align="left">{p.reference_number || "—"}</Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Th({ align, children, theme }) {
  const t = theme || resolveTheme(null);
  return (
    <th style={{
      textAlign: align, padding: "6px 8px", fontWeight: 700,
      color: t.headerText,
      borderBottom: `1px solid ${t.headerBg}`,
    }}>{children}</th>
  );
}

function Td({ align, children }) {
  return (
    <td style={{
      textAlign: align, padding: "5px 8px",
      borderBottom: `1px solid ${BORDER_GRAY}`,
      color: TEXT_BODY,
    }}>{children}</td>
  );
}

function DisabledMenuItem({ label, hint }) {
  return (
    <div title={hint}
         role="menuitem"
         aria-disabled="true"
         style={{
           padding: "8px 12px",
           color: "#888",
           cursor: "not-allowed",
           fontSize: 13,
           borderRadius: 3,
           userSelect: "none",
         }}>
      <div>{label}</div>
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 2, fontStyle: "italic" }}>
        {hint}
      </div>
    </div>
  );
}
